import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Megaphone, Trash2, Plus } from "lucide-react";

export default function AdminBroadcasts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", severity: "info", is_active: true });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => setRows((await api.get("/admin/announcements")).data), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.title || !form.body) return toast.error("Title + body required");
    setBusy(true);
    try {
      await api.post("/admin/announcements", form);
      toast.success("Broadcast published");
      setForm({ title: "", body: "", severity: "info", is_active: true });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this broadcast?")) return;
    await api.delete(`/admin/announcements/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="p-6 space-y-4 text-foreground" data-testid="admin-broadcasts">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// BROADCASTS</div>
        <h1 className="text-3xl font-bold mt-1">Platform announcements</h1>
        <div className="text-muted-foreground text-sm mt-1">Publish a banner that every tenant sees at the top of their app.</div>
      </div>

      <Card className="p-4 rounded-sm bg-card border-border space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Draft new</div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><Label className="text-foreground/80">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Scheduled maintenance Saturday 2 AM IST"
              className="mt-1 bg-background text-foreground border-border" data-testid="broadcast-title" />
          </div>
          <div><Label className="text-foreground/80">Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger className="mt-1 rounded-sm bg-background text-foreground border-border" data-testid="broadcast-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label className="text-foreground/80">Body</Label>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3}
            className="mt-1 w-full bg-background border border-border rounded-sm p-2 text-sm text-foreground" placeholder="We'll be doing DB maintenance…" data-testid="broadcast-body" />
        </div>
        <div className="flex justify-end">
          <Button onClick={create} disabled={busy} className="rounded-sm bg-orange-500 hover:bg-orange-500/90 text-foreground gap-1.5" data-testid="broadcast-publish">
            <Plus className="w-4 h-4" /> Publish
          </Button>
        </div>
      </Card>

      <div className="space-y-2">
        {rows.map((a) => (
          <Card key={a.id} className="p-4 rounded-sm bg-card border-border flex items-start gap-3" data-testid={`bc-${a.id.slice(0,8)}`}>
            <Megaphone className={`w-5 h-5 mt-0.5 ${a.severity === "critical" ? "text-red-600" : a.severity === "warning" ? "text-amber-600" : "text-blue-600"}`} />
            <div className="flex-1">
              <div className="font-bold">{a.title}</div>
              <div className="text-sm text-foreground/80 mt-1">{a.body}</div>
              <div className="text-[10px] font-mono text-muted-foreground/80 mt-2">{a.severity?.toUpperCase()} · by {a.created_by} · {a.created_at?.slice(0, 16).replace("T", " ")} {a.is_active ? "" : "· inactive"}</div>
            </div>
            <button onClick={() => remove(a.id)} className="text-muted-foreground/80 hover:text-red-600" data-testid={`bc-del-${a.id.slice(0,8)}`}><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
        {rows.length === 0 && <div className="text-center py-8 text-muted-foreground/80 italic">No broadcasts yet</div>}
      </div>
    </div>
  );
}
